<template>
  <v-form ref="stepOrderRequestRef">
    <v-row>
      <v-col cols="12" lg="6" xl="4" class="pa-6">
        <v-alert class="mb-6" density="compact" width="fit-content">
          <template #text>
            <b> {{ houseConnectionText }} </b>
            <v-divider class="my-1" />
            Anschlussadresse: <b> {{ initStore.paramsAddressText }} </b>
          </template>
        </v-alert>

        <v-container fluid class="pa-0">
          <template v-if="!bookingStore.isCurrentBookingModeAnyNe4Only">
            <h4 class="mb-2">
              Haustyp<sup v-if="mandatoryBoolean" class="required">*</sup>
            </h4>

            <v-radio-group
              v-model="formOrderRequestStore.isOrderingForMph"
              :rules="mandatoryBoolean"
              class="my-0"
              inline
              @change="mphUpdated"
            >
              <div class="d-flex align-center">
                <v-radio
                  label="Einfamilienhaus"
                  :value="false"
                  :disabled="isOrderingForEfhBlocked"
                />

                <tooltip
                  v-if="!initStore.bookingApproach!.isEfhBookingPossible"
                  message="Für diesen Haushalt wurde bereits ein Produkt gebucht."
                />
              </div>

              <div v-if="!formOrderRequestStore.isMphOrderBlocked" class="d-flex align-center">
                <v-radio
                  label="Mehrparteienhaus"
                  :value="true"
                  :disabled="isOrderingForMphBlocked"
                />

                <tooltip
                  v-if="!initStore.bookingApproach!.isMphBookingPossible"
                  message="Für diesen Haushaltstypen ist keine Buchung möglich."
                />
              </div>
            </v-radio-group>
          </template>

          <template v-if="formOrderRequestStore.isOrderingForMph">
            <h4 class="my-2">
              Innenverkabelung <sup v-if="mandatory" class="required">*</sup>
            </h4>

            <v-radio-group
              v-model="formOrderRequestStore.ne4OrderType"
              class="my-0"
              inline
              :rules="mandatory"
            >
              <v-radio label="Gesamtausbau" value="complete" />
              <v-radio label="Endmontage (GF-GV + GF-TA setzen)" value="endAssembly" />
              <!-- GVM-457: Disable Home-ID-only -->
              <!--<v-radio label="Home-ID-only (Kupfer)" value="copper" />-->
              <!--<v-radio label="Home-ID-only (Glasfaser)" value="fiberOptics" />-->
            </v-radio-group>
          </template>
          <div class="d-flex align-center">
            <v-checkbox
              v-model="formOrderRequestStore.isOrderingProduct"
              label="Glasfaserprodukt direkt im Anschluss mit beauftragen"
              hide-details
              class="flex-unset"
              :disabled="formOrderRequestStore.isProductOrderBlocked"
              @update:model-value="onUpdatedIsOrderingProduct"
            />

            <tooltip
              v-if="formOrderRequestStore.isProductOrderBlocked"
              message="Für diese Option muss zunächst ein Haustyp ausgewählt werden"
            />
          </div>
        </v-container>
        <v-container v-if="!bookingStore.isCurrentBookingModeAnyNe4Only" fluid class="pa-0">
          <v-text-field
            v-model="formOrderRequestStore.houseConnectionPrice"
            class="py-4 euro"
            label="Einmalige Hausanschlusskosten"
            readonly
            append-icon="mdi-currency-eur"
            variant="underlined"
            :persistent-hint="!!formOrderRequestStore.isOrderingForMph"
            :hide-details="!formOrderRequestStore.isOrderingForMph"
            :hint="hintHouseConnectionPrice"
          />

          <template v-if="+formOrderRequestStore.houseConnectionPrice > 0">
            <v-checkbox
              v-model="acceptHouseConnectionCosts"
              hide-details="auto"
              :rules="mandatory"
            >
              <template #label>
                <span>Kunde wurde auf Hausanschlusskosten hingewiesen<sup
                  v-if="mandatory"
                  class="required"
                >*</sup></span>
              </template>
            </v-checkbox>

            <v-text-field
              v-if="!formOrderRequestStore.isBankTransferRequested"
              v-model="formOrderRequestStore.iban"
              v-maska="ibanMask"
              hide-details="auto"
              :rules="iban"
            >
              <template #label>
                <span>IBAN<sup v-if="iban" class="required">*</sup></span>
              </template>
            </v-text-field>

            <div class="d-flex align-center">
              <v-checkbox
                v-model="formOrderRequestStore.isBankTransferRequested"
                label="Kunde wünscht Zahlung per Überweisung"
                hide-details
                :disabled="isDisabledBankTransferRequested"
                :rules="formOrderRequestStore.iban ? [] : mandatory"
                class="flex-unset"
              />

              <tooltip
                v-if="isDisabledBankTransferRequested"
                message="Für Mehrparteienhaushalte findet eine Zahlung immer per Überweisung statt."
              />
            </div>
          </template>

          <v-text-field
            v-model="formOrderRequestStore.houseConnectionLocation"
            class="py-4"
            hide-details="auto"
            :rules="mandatory"
          >
            <template #label>
              <span>Wo befindet sich der Hausanschluss?<sup
                v-if="mandatory"
                class="required"
              >*</sup></span>
            </template>
          </v-text-field>
        </v-container>
      </v-col>
    </v-row>
  </v-form>
</template>

<script setup lang="ts">
import type { MaskInputOptions } from 'maska';
import {
  computed,
  inject,
  reactive,
  ref,
} from 'vue';
import Tooltip from './common/custom-tooltip.vue';
import { priceWithoutVat } from '../composables/formatter';
import { mandatory, mandatoryBoolean, iban } from '../composables/validations';
import { UpdateBookingModeKey } from '../configs/symbols';
import useBookingStore from '../store/booking';
import useFormOrderRequestStore from '../store/form-order-request';
import useInitStore from '../store/init';

import { BookingMode, type BookingModeFunc } from '../types/order-main';
import useOrderStore from '../store/order';

const bookingStore = useBookingStore();
const initStore = useInitStore();
const formOrderRequestStore = useFormOrderRequestStore();

const updateBookingMode = inject<BookingModeFunc>(UpdateBookingModeKey) as BookingModeFunc;

const ibanMask = reactive<MaskInputOptions>({
  mask: 'DE## #### #### #### #### ##',
  eager: true,
});
const stepOrderRequestRef = ref();
const acceptHouseConnectionCosts = ref(false);

const disableHouseTypeSelection = computed(
  () => initStore.initialBookingMode === BookingMode.houseConnectionAndProduct,
);

const isOrderingForEfhBlocked = computed(
  () => !initStore.bookingApproach!.isEfhBookingPossible || disableHouseTypeSelection.value,
);

const isOrderingForMphBlocked = computed(
  () => !initStore.bookingApproach!.isMphBookingPossible || disableHouseTypeSelection.value,
);

const isDisabledBankTransferRequested = ref<boolean>(bookingStore.isCurrentBookingModeAnyNe4Only);

const houseConnectionText = computed(() => {
  if (bookingStore.isCurrentBookingModeAnyNe4Only) {
    return initStore.bookingApproach!.houseConnectionBooked
      ? 'Hausanschluss bereits beauftragt'
      : 'Laut Auftraggeber wurde der Hausanschluss bereits gesetzt';
  }

  return 'FTTH-Hausanschluss beauftragen';
});

const hintHouseConnectionPrice = computed(() => {
  const { houseConnectionPrice } = formOrderRequestStore;
  return `Exkl. Mehrwehrtsteuer: ${priceWithoutVat(houseConnectionPrice)} €`;
});

function updateBookingModeFromProductChange() {
  const { initialBookingMode } = initStore;
  const {
    isOrderingForMph,
    isOrderingProduct,
  } = formOrderRequestStore;

  switch (initialBookingMode) {
    case BookingMode.houseConnectionOnly:
      if (isOrderingForMph && isOrderingProduct) {
        updateBookingMode(BookingMode.houseConnectionAndNe4AndProduct);
      } else if (isOrderingForMph) {
        updateBookingMode(BookingMode.houseConnectionAndNe4);
      } else if (isOrderingProduct) {
        updateBookingMode(BookingMode.houseConnectionAndProduct);
      } else {
        updateBookingMode(initialBookingMode);
      }
      break;
    case BookingMode.ne4Only:
      updateBookingMode(isOrderingProduct ? BookingMode.ne4AndProduct : initialBookingMode);
      break;
    case BookingMode.houseConnectionAndProduct:
      updateBookingMode(
        isOrderingForMph
          ? BookingMode.houseConnectionAndNe4AndProduct
          : initialBookingMode,
      );
      break;
    default:
      break;
  }
}

async function onUpdatedIsOrderingProduct(newValue: boolean | null) {
  formOrderRequestStore.updateHouseConnectionPrice();
  updateBookingModeFromProductChange();
  if (newValue && !bookingStore.articles.length) {
    await bookingStore.loadArticles();
  }
}

function mphUpdated() {
  if (initStore.initialBookingMode !== BookingMode.houseConnectionAndProduct) {
    formOrderRequestStore.isProductOrderBlocked = false;
    if (!initStore.bookingApproach!.isProductBookingPossible) {
      formOrderRequestStore.isOrderingProduct = false;
      formOrderRequestStore.isProductOrderBlocked = true;
    }
  }

  if (!formOrderRequestStore.isOrderingForMph) {
    formOrderRequestStore.ne4OrderType = '';
    isDisabledBankTransferRequested.value = false;
  } else {
    formOrderRequestStore.isBankTransferRequested = true;
    isDisabledBankTransferRequested.value = true;
  }

  onUpdatedIsOrderingProduct(formOrderRequestStore.isOrderingProduct);
}

defineExpose({
  applyData: () => {
    useOrderStore().addOrderRequestData(formOrderRequestStore.stepData);
  },
  isValid: async (): Promise<boolean> => (await stepOrderRequestRef.value.validate()).valid,
});
</script>

<style scoped>
  .flex-unset {
    flex: unset !important;
  }
</style>
